import React, { useContext, useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import PendingIcon from '@mui/icons-material/Pending'
import { apiUrl } from '../../../../config/api'
import { clientContext } from '../../../../contexts/ClientContext'
import { notifyBulkDocumentUpload } from '../../../../services/documents'

const UploadStatusDialog = ({
  open,
  config,
  handleClose,
  clientId,
  categoryId,
  subcategoryId,
  assignedBy,
  assignedTo,
  fileId,
  notify,
  recipients
}) => {
  const [results, setResults] = useState({}) // Stores { index: 'pending' | 'uploading' | 'success' | 'error' }

  const { refreshAddedDoc } = useContext(clientContext)

  useEffect(() => {
    if (open) {
      startUploads()
    }
  }, [open])

  const startUploads = async () => {
    const initialStatus = {}
    config.forEach((_, i) => (initialStatus[i] = 'pending'))
    setResults(initialStatus)

    const uploadedDocs = []

    for (let i = 0; i < config.length; i++) {
      setResults((prev) => ({ ...prev, [i]: 'uploading' }))

      try {
        const uploaded = await uploadFile(config[i])
        uploadedDocs.push({ id: uploaded.id, filename: config[i].filename })
        setResults((prev) => ({ ...prev, [i]: 'success' }))
      } catch (error) {
        console.error('Upload failed for index', i, error)
        setResults((prev) => ({ ...prev, [i]: 'error' }))
      }
    }

    // Single notification for the whole batch, not one per file
    if (notify && recipients?.length > 0 && uploadedDocs.length > 0) {
      try {
        await notifyBulkDocumentUpload({
          clientId,
          assignedTo,
          assignedBy,
          brandLabel: window.$brand,
          documents: uploadedDocs,
          recipients
        })
      } catch (error) {
        console.error('Bulk upload notification failed', error)
      }
    }
  }

  const uploadFile = async (fileData) => {
    const { originalFile, filename, notes, expiration, visible } = fileData

    const formData = new FormData()

    formData.append('file', originalFile)

    const fields = {
      filename,
      clientId,
      categoryId,
      subcategoryId,
      assignedBy,
      assignedTo,
      notes,
      expiration,
      visible,
      fileId,
      brandLabel: window.$brand
    }

    Object.entries(fields).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value)
      }
    })

    let url = apiUrl('UploadAndInsertDocument')
    const response = await fetch(url, {
      method: 'POST',
      body: formData
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(
        errorText || `Upload failed with status ${response.status}`
      )
    }

    return response.json() // { id, url, modified }
  }

  const isFinished =
    Object.values(results).length > 0 &&
    Object.values(results).every(
      (status) => status === 'success' || status === 'error'
    )

  const hasErrors = Object.values(results).some((status) => status === 'error')

  const handleFinish = () => {
    handleClose()
    refreshAddedDoc()
  }

  return (
    <Dialog open={open} fullWidth maxWidth='xs'>
      <DialogTitle>
        {!isFinished
          ? 'Uploading Documents...'
          : hasErrors
            ? 'Upload Finished with Errors'
            : 'Upload Complete'}
      </DialogTitle>

      <DialogContent dividers>
        <List>
          {config.map((item, index) => (
            <React.Fragment key={index}>
              <ListItem>
                <ListItemIcon>
                  {results[index] === 'uploading' && (
                    <CircularProgress size={24} />
                  )}
                  {results[index] === 'pending' && (
                    <PendingIcon color='disabled' />
                  )}
                  {results[index] === 'success' && (
                    <CheckCircleIcon color='success' />
                  )}
                  {results[index] === 'error' && <ErrorIcon color='error' />}
                </ListItemIcon>
                <ListItemText
                  primary={item.filename}
                  secondary={results[index]?.toUpperCase()}
                  secondaryTypographyProps={{
                    sx: {
                      fontSize: '0.7rem',
                      fontWeight: 'bold',
                      color:
                        results[index] === 'success'
                          ? 'success.main'
                          : 'inherit'
                    }
                  }}
                />
              </ListItem>
              {index < config.length - 1 && <Divider component='li' />}
            </React.Fragment>
          ))}
        </List>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          fullWidth
          variant='contained'
          color={isFinished && hasErrors ? 'warning' : 'primary'}
          disabled={!isFinished}
          onClick={handleFinish}
        >
          {!isFinished
            ? 'Processing...'
            : hasErrors
              ? 'Close (some failed)'
              : 'Finish'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UploadStatusDialog
